import * as React from 'react';
import {
  CreditCard,
  ExternalLink,
  Loader2,
  Receipt,
  Users,
  Wallet,
} from 'lucide-react';
import type { BillingSummary } from '@proptr/api-client';
import { AppShell } from '../components/layout/AppShell.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { SectionCard } from '../components/SectionCard.js';
import { Select, type SelectOption } from '../components/ui/select.js';
import { client } from '../main.js';

// Stripe subscription billing (test mode). Plan state lives on
// enterprise.billing, stamped by the API's confirm flow + webhook.
// Payment method and invoices are handled by the hosted Stripe billing
// portal rather than in-app UI.

type PlanKey = 'starter' | 'pro' | 'scale';

const PLANS: Array<{
  key: PlanKey;
  name: string;
  price: number;
  blurb: string;
}> = [
  {
    key: 'starter',
    name: 'Starter',
    price: 49,
    blurb: 'Up to 5 agent proptrees · agency proptree · CRM sync',
  },
  {
    key: 'pro',
    name: 'Pro',
    price: 99,
    blurb: 'Up to 25 agents · custom forms · 5 affiliates · lead push',
  },
  {
    key: 'scale',
    name: 'Scale',
    price: 249,
    blurb: 'Unlimited agents · 20 affiliates · custom domain · SLA',
  },
];
const money = (cents: number, currency: string) =>
  (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  });

const ADDON_PACKS: Array<{ pack: 0 | 5 | 10 | 20 | 30; price: number }> = [
  { pack: 0, price: 0 },
  { pack: 5, price: 25 },
  { pack: 10, price: 45 },
  { pack: 20, price: 80 },
  { pack: 30, price: 105 },
];

export function BillingPage() {
  const [summary, setSummary] = React.useState<BillingSummary | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  // A proposed paid change awaiting explicit user confirmation. Nothing
  // is charged until the user confirms in the popup.
  const [pending, setPending] = React.useState<
    | {
        kind: 'plan';
        plan: PlanKey;
        label: string;
        dueNow: number;
        currency: string;
        newMonthly: number;
      }
    | {
        kind: 'addon';
        pack: 0 | 5 | 10 | 20 | 30;
        label: string;
        dueNow: number;
        currency: string;
        newMonthly: number;
      }
    | {
        kind: 'cancel';
        label: string;
        refundAmount: number;
        currency: string;
      }
    | null
  >(null);
  const [confirming, setConfirming] = React.useState(false);
  const [busy, setBusy] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const fromPortal = params.get('portal') === 'done';

    const load = async () => {
      try {
        if (sessionId) {
          // Back from Checkout — stamp the subscription first.
          await client.billing.confirm(sessionId).catch(() => undefined);
          window.history.replaceState(null, '', '/billing');
        }
        const s = await client.billing.summary({
          sync: fromPortal || Boolean(sessionId),
        });
        if (alive) setSummary(s);
      } catch (err) {
        if (alive) setError((err as Error).message);
      }
    };
    void load();
    return () => {
      alive = false;
    };
  }, []);

  async function selectPlan(plan: PlanKey) {
    setBusy(plan);
    setError(null);
    setNotice(null);
    try {
      if (summary?.plan && summary.plan !== 'trial') {
        // Already subscribed — preview the cost and ask first.
        const preview = await client.billing.previewChange({ plan });
        const meta = PLANS.find((p) => p.key === plan)!;
        setPending({
          kind: 'plan',
          plan,
          label: `Switch to Proptree ${meta.name}`,
          ...preview,
        });
        setBusy(null);
      } else {
        const { url } = await client.billing.checkout(plan);
        window.location.href = url;
      }
    } catch (err) {
      setError((err as Error).message);
      setBusy(null);
    }
  }

  async function selectAddon(pack: 0 | 5 | 10 | 20 | 30) {
    setBusy(`addon-${pack}`);
    setError(null);
    setNotice(null);
    try {
      const preview = await client.billing.previewChange({ pack });
      setPending({
        kind: 'addon',
        pack,
        label:
          pack === 0 ? 'Remove affiliate boost' : `Boost: +${pack} affiliates`,
        ...preview,
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function openCancel() {
    setBusy('cancel-preview');
    setError(null);
    setNotice(null);
    try {
      const preview = await client.billing.cancelPreview();
      setPending({
        kind: 'cancel',
        label: 'Cancel subscription',
        refundAmount: preview.refundAmount,
        currency: preview.currency,
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function resumeSubscription() {
    setBusy('resume');
    setError(null);
    setNotice(null);
    try {
      await client.billing.resume();
      const s = await client.billing.summary();
      setSummary(s);
      setNotice('Subscription resumed — it renews as normal.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function confirmPending() {
    if (!pending) return;
    setConfirming(true);
    setError(null);
    try {
      if (pending.kind === 'cancel') {
        const { refunded, currency } = await client.billing.cancel();
        const s = await client.billing.summary();
        setSummary(s);
        setNotice(
          refunded > 0
            ? `Subscription cancelled — ${money(refunded, currency)} for the unused part of the month was refunded to your card. Refunds take 5–10 days to appear.`
            : 'Subscription cancelled. There was no unused time to refund.',
        );
        setPending(null);
        return;
      }
      if (pending.kind === 'plan') {
        await client.billing.changePlan(pending.plan);
      } else {
        await client.billing.affiliateAddon(pending.pack);
      }
      const s = await client.billing.summary();
      setSummary(s);
      setNotice(
        pending.dueNow > 0
          ? `${pending.label} — ${money(pending.dueNow, pending.currency)} was charged to your card. From next cycle it's one combined invoice of ${money(pending.newMonthly, pending.currency)}/mo.`
          : `${pending.label} — done. Any unused amount is credited on your next invoice. New total: ${money(pending.newMonthly, pending.currency)}/mo.`,
      );
      setPending(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setConfirming(false);
    }
  }

  async function openPortal() {
    setBusy('portal');
    try {
      const { url } = await client.billing.portal();
      window.location.href = url;
    } catch (err) {
      setError((err as Error).message);
      setBusy(null);
    }
  }

  const loading = summary === null && error === null;
  const isPro = summary?.plan !== undefined && summary.plan !== 'trial';
  const currentPlan = PLANS.find((p) => p.key === summary?.plan);
  const renewsAt = summary?.renewsAt
    ? new Date(summary.renewsAt).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : null;

  return (
    <AppShell>
      <div className="space-y-6 pb-10">
        <div className="min-w-0">
          <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
            Billing
          </h1>
          <p className="mt-3 font-mono text-[11px] text-muted-foreground">
            Manage your plan, payment method, and invoice history.
          </p>
        </div>

        {error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 font-mono text-[11px] text-destructive">
            {error}
          </div>
        ) : null}
        {notice ? (
          <div className="rounded-md border border-success/40 bg-success/10 p-3 font-mono text-[11px] text-foreground">
            {notice}
          </div>
        ) : null}

        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <SectionCard
              title="Plan"
              subtitle="Your subscription"
              icon={Wallet}
              collapsible={false}
            >
              <div className="grid grid-cols-12 gap-4 p-5">
                <div className="col-span-12">
                  <div className="mono-label text-muted-foreground">
                    Current plan
                  </div>
                  <div className="mt-1 font-display text-[20px] font-medium leading-tight text-foreground">
                    {loading
                      ? '…'
                      : currentPlan
                        ? `Proptree ${currentPlan.name}`
                        : 'Trial'}
                  </div>
                  <p className="mt-1 font-mono text-[10px] leading-relaxed text-muted-foreground">
                    {loading
                      ? 'Loading subscription…'
                      : currentPlan
                        ? `$${currentPlan.price}/month · ${summary?.status}${
                            renewsAt ? ` · renews ${renewsAt}` : ''
                          }`
                        : summary?.configured
                          ? 'Pick a plan below to get started.'
                          : 'Stripe is not configured on the API.'}
                  </p>
                </div>
                {isPro && summary?.cancelAtPeriodEnd && (
                  <div className="col-span-12">
                    <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 font-mono text-[10px] leading-relaxed text-foreground">
                      Scheduled to end on {renewsAt ?? 'the period end'} — set
                      outside the app. Resume to keep it active.
                    </div>
                  </div>
                )}
                {isPro && (
                  <div className="col-span-12 flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy !== null}
                      onClick={openPortal}
                    >
                      {busy === 'portal' ? (
                        <Loader2 className="animate-spin" />
                      ) : (
                        <ExternalLink />
                      )}
                      Manage subscription
                    </Button>
                    {summary?.cancelAtPeriodEnd ? (
                      <Button
                        type="button"
                        size="sm"
                        disabled={busy !== null}
                        onClick={resumeSubscription}
                      >
                        {busy === 'resume' ? (
                          <Loader2 className="animate-spin" />
                        ) : null}
                        Resume subscription
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        disabled={busy !== null}
                        onClick={openCancel}
                      >
                        Cancel plan
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </SectionCard>

            <SectionCard
              title="Affiliates"
              subtitle="Affiliate proptrees in use on your plan"
              icon={Users}
              collapsible={false}
            >
              <div className="p-5">
                {(() => {
                  const included = summary?.affiliatesIncluded ?? 0;
                  const addon = summary?.affiliateAddon ?? 0;
                  const total = included + addon;
                  const used = summary?.affiliatesUsed ?? 0;
                  const pct =
                    total > 0 ? Math.min(100, (used / total) * 100) : 0;
                  const full = total > 0 && used >= total;
                  return (
                    <>
                      <div className="flex items-baseline justify-between">
                        <div className="numeral font-display text-[20px] font-medium leading-none">
                          {used}
                          <span className="text-muted-foreground"> / {total}</span>
                        </div>
                        <div
                          className={`font-mono text-[10px] font-semibold ${
                            full ? 'text-warning' : 'text-muted-foreground'
                          }`}
                        >
                          {full
                            ? 'Limit reached'
                            : `${Math.max(0, total - used)} left`}
                        </div>
                      </div>
                      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          className={`h-full rounded-full transition-[width] duration-500 ease-out ${
                            full ? 'bg-warning' : 'bg-primary'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <p className="mt-2 font-mono text-[10px] text-muted-foreground">
                        {included} with plan · +{addon} boost
                      </p>
                      <div className="mt-4">
                        <div className="mono-label mb-1.5 text-muted-foreground">
                          Affiliate boost
                        </div>
                        <Select
                          className="w-full"
                          disabled={busy !== null || !isPro}
                          locked={!isPro}
                          searchable={false}
                          placeholder="Requires an active plan"
                          options={ADDON_PACKS.map<SelectOption>(
                            ({ pack, price }) => ({
                              value: String(pack),
                              label:
                                pack === 0
                                  ? 'No boost'
                                  : `+${pack} affiliates — $${price}/mo`,
                            }),
                          )}
                          value={
                            isPro ? String(summary?.affiliateAddon ?? 0) : null
                          }
                          onChange={(v) =>
                            selectAddon(Number(v) as 0 | 5 | 10 | 20 | 30)
                          }
                        />
                        <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                          {isPro
                            ? 'Billed with your plan on one invoice. Changes charge the prorated difference immediately.'
                            : 'Boosts add affiliate proptrees on top of a paid plan. Choose a plan below to unlock.'}
                        </p>
                      </div>
                    </>
                  );
                })()}
              </div>
            </SectionCard>

            <SectionCard
              title="Payment method"
              subtitle="Card on file used for monthly charges"
              icon={CreditCard}
              collapsible={false}
            >
              <div className="p-5">
                {summary?.paymentMethod ? (
                  <Card className="flex items-center justify-between gap-3 px-4 py-5">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-12 items-center justify-center rounded-md border border-border bg-muted/40 font-mono text-[9px] font-bold uppercase">
                        {summary.paymentMethod.brand}
                      </div>
                      <div>
                        <div className="font-mono text-[13px] font-semibold tabular-nums">
                          •••• {summary.paymentMethod.last4}
                        </div>
                        <div className="font-mono text-[10px] text-muted-foreground">
                          Expires{' '}
                          {String(summary.paymentMethod.expMonth).padStart(2, '0')}
                          /{summary.paymentMethod.expYear}
                        </div>
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy !== null}
                      onClick={openPortal}
                    >
                      <ExternalLink />
                      Manage
                    </Button>
                  </Card>
                ) : (
                  <Card className="flex items-center justify-between gap-3 border-dashed bg-muted/30 px-4 py-6">
                    <div className="font-sans text-[13px] text-muted-foreground">
                      No card on file. Subscribe to add one.
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy !== null || !summary?.hasCustomer || !isPro}
                      onClick={openPortal}
                    >
                      <ExternalLink />
                      Manage
                    </Button>
                  </Card>
                )}
              </div>
            </SectionCard>
          </div>

          <SectionCard
            title="Plans"
            subtitle="Choose the plan that fits your team"
            icon={Wallet}
            collapsible={false}
          >
            <div className="grid gap-4 p-5 md:grid-cols-3">
              {PLANS.map((plan) => {
                const isCurrent = summary?.plan === plan.key;
                return (
                  <div
                    key={plan.key}
                    className={`relative flex flex-col rounded-lg border p-5 ${
                      isCurrent
                        ? 'border-primary/50 bg-primary/5'
                        : 'border-border bg-card'
                    }`}
                  >
                    {isCurrent && (
                      <span className="mono-label absolute right-4 top-4 inline-flex items-center rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-primary">
                        Current
                      </span>
                    )}
                    <div className="mono-label text-muted-foreground">
                      {plan.name}
                    </div>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="numeral font-display text-[28px] font-medium leading-none tracking-[-0.02em]">
                        ${plan.price}
                      </span>
                      <span className="font-mono text-[10px] text-muted-foreground">
                        / month
                      </span>
                    </div>
                    <p className="mt-3 flex-1 font-sans text-[12px] leading-relaxed text-muted-foreground">
                      {plan.blurb}
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant={isCurrent ? 'outline' : 'default'}
                      className="mt-4 w-full"
                      disabled={
                        busy !== null || isCurrent || !summary?.configured
                      }
                      onClick={() => selectPlan(plan.key)}
                    >
                      {busy === plan.key ? (
                        <Loader2 className="animate-spin" />
                      ) : null}
                      {isCurrent
                        ? 'Current plan'
                        : isPro
                          ? `Switch to ${plan.name}`
                          : `Subscribe — $${plan.price}/mo`}
                    </Button>
                  </div>
                );
              })}
            </div>
            <p className="px-5 pb-4 font-mono text-[10px] text-muted-foreground">
              Switching plans is prorated on your next invoice.
            </p>
          </SectionCard>

          <SectionCard
            title="Invoices & credit notes"
            subtitle="Charges, refunds, and downloadable documents"
            icon={Receipt}
            collapsible={false}
          >
            <div className="p-5">
              {(() => {
                // Invoices and credit notes are separate accounting
                // documents; list both, newest first.
                const docs = [
                  ...(summary?.invoices ?? []).map((inv) => ({
                    kind: 'invoice' as const,
                    id: inv.id,
                    number: inv.number ?? inv.id,
                    sub: null as string | null,
                    created: inv.created,
                    amount: inv.total,
                    currency: inv.currency,
                    status: inv.status ?? '—',
                    url: inv.url,
                  })),
                  ...(summary?.creditNotes ?? []).map((note) => ({
                    kind: 'credit' as const,
                    id: note.id,
                    number: note.number ?? note.id,
                    sub: note.invoiceNumber
                      ? `Credit note · against ${note.invoiceNumber}`
                      : 'Credit note',
                    created: note.created,
                    amount: note.total,
                    currency: note.currency,
                    status: 'refunded',
                    url: note.url,
                  })),
                ].sort((a, b) => (a.created < b.created ? 1 : -1));

                if (!docs.length) {
                  return (
                    <Card className="border-dashed bg-muted/30 px-4 py-8 text-center font-sans text-[13px] text-muted-foreground">
                      No invoices yet.
                    </Card>
                  );
                }
                return (
                  <div className="divide-y divide-[hsl(var(--hairline))] rounded-lg border border-border">
                    {docs.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center gap-4 px-4 py-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-mono text-[12px] font-semibold">
                            {doc.number}
                          </div>
                          <div className="font-mono text-[10px] text-muted-foreground">
                            {new Date(doc.created).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                            {doc.sub ? ` · ${doc.sub}` : ''}
                          </div>
                        </div>
                        <span
                          className={`mono-label inline-flex items-center rounded-full border px-2 py-0.5 ${
                            doc.kind === 'credit'
                              ? 'border-warning/40 bg-warning/10 text-warning'
                              : doc.status === 'paid'
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                : 'border-border bg-muted text-muted-foreground'
                          }`}
                        >
                          {doc.status}
                        </span>
                        <div className="w-24 text-right font-mono text-[13px] font-semibold tabular-nums">
                          {doc.kind === 'credit' ? '−' : ''}
                          {money(Math.abs(doc.amount), doc.currency)}
                        </div>
                        {doc.url ? (
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noreferrer"
                            className="font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-foreground underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground"
                          >
                            {doc.kind === 'credit' ? 'PDF' : 'View'}
                          </a>
                        ) : null}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </SectionCard>
        </div>
      </div>

      {pending && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={() => (confirming ? null : setPending(null))}
        >
          <Card
            className="w-full max-w-[440px] p-0 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-[hsl(var(--hairline))] px-5 pb-3 pt-4">
              <div className="font-sans text-[15px] font-semibold text-foreground">
                {pending.label}
              </div>
              <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                Review the charge before confirming.
              </p>
            </div>
            {pending.kind === 'cancel' ? (
              <div className="space-y-3 px-5 py-4">
                <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-4 py-3">
                  <span className="font-sans text-[13px] text-muted-foreground">
                    Refunded to your card
                  </span>
                  <span className="font-mono text-[15px] font-semibold tabular-nums">
                    {money(pending.refundAmount, pending.currency)}
                  </span>
                </div>
                <p className="font-sans text-[13px] leading-relaxed text-foreground">
                  Your plan ends <span className="font-semibold">immediately</span>{' '}
                  and the unused part of this month goes back to
                  {summary?.paymentMethod
                    ? ` your ${summary.paymentMethod.brand.toUpperCase()} ending ${summary.paymentMethod.last4}`
                    : ' your card'}
                  . Your affiliate boost ends with it.
                </p>
                <p className="font-mono text-[10px] leading-relaxed text-muted-foreground">
                  Refunds usually appear in 5–10 days. You can subscribe again
                  any time — nothing is deleted.
                </p>
              </div>
            ) : (
              <div className="space-y-3 px-5 py-4">
                <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-4 py-3">
                  <span className="font-sans text-[13px] text-muted-foreground">
                    {pending.dueNow > 0
                      ? 'Charged now (prorated)'
                      : 'Credited on next invoice'}
                  </span>
                  <span className="font-mono text-[15px] font-semibold tabular-nums">
                    {money(Math.abs(pending.dueNow), pending.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
                  <span className="font-sans text-[13px] text-muted-foreground">
                    New monthly total
                  </span>
                  <span className="font-mono text-[15px] font-semibold tabular-nums">
                    {money(pending.newMonthly, pending.currency)}/mo
                  </span>
                </div>
                <p className="font-mono text-[10px] leading-relaxed text-muted-foreground">
                  Billed together with your plan as one invoice, charged to
                  the card on file.
                </p>
              </div>
            )}
            <div className="flex items-center justify-end gap-2 px-5 py-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={confirming}
                onClick={() => setPending(null)}
              >
                {pending.kind === 'cancel' ? 'Keep my plan' : 'Cancel'}
              </Button>
              <Button
                type="button"
                size="sm"
                className={
                  pending.kind === 'cancel'
                    ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                    : 'bg-gray-950 text-white hover:bg-gray-800'
                }
                disabled={confirming}
                onClick={confirmPending}
              >
                {confirming ? <Loader2 className="animate-spin" /> : null}
                {pending.kind === 'cancel'
                  ? pending.refundAmount > 0
                    ? `Cancel & refund ${money(pending.refundAmount, pending.currency)}`
                    : 'Cancel subscription'
                  : pending.dueNow > 0
                    ? `Confirm & pay ${money(pending.dueNow, pending.currency)}`
                    : 'Confirm'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </AppShell>
  );
}
